import React, { useCallback, useMemo, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Menu from "@mui/material/Menu";

import COMMON from "common";
import api from "services/api";
import sanitizeObject from "common/sanitize-object";
import { formatDate } from "common/calendar";
import serveRequestErrors from "common/serve-request-errors";
import { formatCurrency } from "common/format-currency-pattern";
import AppEmptyState from "components/app-empty-state";
import AppClaimModal from "components/pages/claim-management/app-claim-modal";
import AppTable, { AppTableCell, AppTableMoreIcon } from "components/app-table";
import previewIcon from "assets/images/preview-icon.svg";
import removeIcon from "assets/images/remove-icon.svg";
import pdfIcon from "assets/images/pages/leave-management/pdf-icon.svg";
import { MenuItem } from "@mui/material";
import AppCancelClaimModal from "components/pages/claim-management/app-cancel-claim-modal";

const PageClaimPending = (props) => {
	const claimModalRef = useRef();
	const cancelClaimRef = useRef();
	const [searchParams, setSearchParams] = useSearchParams();
	const memoSetSearchParams = useRef(setSearchParams);
	const paramsRef = useRef({
		page: parseInt(searchParams.get("page")) || 1,
		sort: searchParams.get("sort") || "",
	});
	const [anchorEl, setAnchorEl] = useState(null);
	const [selectedId, setSelectedId] = useState(null);
	const [data, setData] = useState({ page: 0, size: 10, total: 0, offset: 0, totalElements: 0, prev: false, next: false, items: [] });
	const cancelRequest = useMemo(() => props.onHandleCancelRequest, [props.onHandleCancelRequest]);
	const isEmptyState = useMemo(() => COMMON.TABLE_REQUEST_STATUS.INSTANCE === data.status, [data.status]);

	const onHandleGetList = useCallback(async () => {
		let response = null;

		setData((prev) => ({ ...prev, items: [] }));

		try {
			const payload = { ...paramsRef.current, size: 10 };

			memoSetSearchParams.current(sanitizeObject(payload), { replace: true });

			payload.page = paramsRef.current.page - 1;

			response = await api.get.claims.history(sanitizeObject({ ...payload, statusListStr: "PENDING" }));
		} catch (error) {
			serveRequestErrors(error);
		}

		if (response) {
			setData((prev) => ({
				...prev,
				page: paramsRef.current.page,
				prev: !response.first,
				next: !response.last,
				items: response.content,
				total: response.totalPages,
				totalElements: response.totalElements,
				offset: response?.pageable?.offset || 0,
				status: response?.pageable,
			}));
		}
	}, []);

	//prettier-ignore
	const onHandlePagination = useCallback((event) => {
		const control = event.currentTarget?.getAttribute("data-ctrl");

		if (control) {
			if (control === "prev") {
				if (paramsRef.current.page <= 1) return;
				paramsRef.current.page -= 1;
			} else {
				if (paramsRef.current.page >= data.total) return;
				paramsRef.current.page += 1;
			}
		} else {
			paramsRef.current.page = event.target.value;
		}

		onHandleGetList();
	}, [onHandleGetList, data.total]);

	const onHandleViews = () => {
		setAnchorEl(null);
		claimModalRef.current.onHandleShow(selectedId);
	};

	const onHandleCancel = () => {
		setAnchorEl(null);
		cancelClaimRef.current.onHandleShow(selectedId);
	};

	const onHandleTableActionMenu = (event, row) => {
		setSelectedId(row.original.id);
		setAnchorEl(event.currentTarget);
	};

	const onHandleCloseActionMenu = () => {
		setAnchorEl(null);
	};

	const AmountCell = useCallback(({ row }) => <AppTableCell right value={formatCurrency(row.original.claimValue)} />, []);

	const SubmitDate = useCallback(({ row }) => <AppTableCell center value={formatDate(row.original.createdDate)} />, []);

	//prettier-ignore
	const AttachmentCell = useCallback(({ row }) => {
		const file = row.original?.companyClaimApplicationFiles?.[0];
		const value = file ? (<a href={file?.fileURL} target="_blank" rel="noopener noreferrer"><img className="table__file" src={pdfIcon} alt="attacment" /></a>) : ("-");
		return (
			<AppTableCell center value={value} />
		);
	}, []);

	//prettier-ignore
	const EInvoiceExemptedCell = useCallback(({ row }) => <AppTableCell center value={row.original.eInvoiceExempted ? "Yes" : "No"} />, []);

	//prettier-ignore
	const MenuViewCell = useCallback(({ row }) => {
		return <AppTableMoreIcon onClick={(event) => onHandleTableActionMenu(event, row)} />;
	}, []);

	//prettier-ignore
	const columns = useMemo(() => [
		{
			Header: "Claim Type",
			accessor: "claimTypeMapped",
			disableSortBy: true,
		},
		{
			Header: "Submit Date",
			accessor: "createdDate",
			disableSortBy: true,
			Cell: SubmitDate,
		},
		{
			Header: () => <div className="table__amount">Amount (RM)</div>,
			accessor: "claimValue",
			disableSortBy: true,
			Cell: AmountCell
		},
        {
			Header: () => <div className="table__attachment">Attachment</div>,
			accessor: "companyClaimApplicationFiles",
			disableSortBy: true,
			Cell: AttachmentCell,
		},
		{
			Header: () => <div className="table__e-invoice-exempted">E-Invoice Exempted</div>,
			accessor: "eInvoiceExempted",
			disableSortBy: true,
			Cell: EInvoiceExemptedCell,
		},
        {
			Header: "",
			accessor: "*",
			disableSortBy: true,
			Cell: MenuViewCell,
		},
	], [SubmitDate, AmountCell, AttachmentCell, MenuViewCell, EInvoiceExemptedCell]);

	useEffect(() => {
		onHandleGetList();
	}, [onHandleGetList]);

	useEffect(() => {
		return () => {
			cancelRequest(COMMON.ENDPOINT_PATH.CLAIMS.HISTORY);
		};
	}, [cancelRequest]);

	return (
		<div className="page-claim-pending">
			<div className="claim-pending">
				{isEmptyState && <AppEmptyState title="No records found" description="You don’t have any records yet" disabledButton />}
				{!isEmptyState && <AppTable columns={columns} pages={data} onHandlePagination={onHandlePagination}/>}
			</div>

			<Menu classes={{ root: "app-table-menu" }} anchorEl={anchorEl} open={!!anchorEl} onClose={onHandleCloseActionMenu} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
				<MenuItem onClick={onHandleViews}><img className="app-table-menu__icon" src={previewIcon} alt="preview" />View</MenuItem>
				<MenuItem onClick={onHandleCancel}><img className="app-table-menu__icon" src={removeIcon} alt="cancel" />Cancel</MenuItem>
			</Menu>

			<AppClaimModal ref={claimModalRef} />
			<AppCancelClaimModal ref={cancelClaimRef} onHandleGetList={onHandleGetList}/>
		</div>
	);
};

export default PageClaimPending;
